import { usePermissions } from '../../permissions/usePermissions';
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Button, message, Card, Space, Typography, Divider } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const Debugging = () => {
  const { can } = usePermissions();
  const allowed = useCallback((name, action = 'read') => {
    const module = name === 'avoir3' ? 'returnCounters' : /tree/i.test(name) ? 'tree' : null;
    return !!module && (can(module, action) || can('counters', action));
  }, [can]);
  const [treeCounters, setTreeCounters] = useState([]);
  const [counterValues, setCounterValues] = useState({});
  const [loading, setLoading] = useState(false);
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;
  
  const fetchTreeCounters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/counter/getall`);
      const filteredTreeCounters = response.data.filter(counter => allowed(counter.name));
      
      // Initialize the local state for counter values
      const newCounterValues = {};
      filteredTreeCounters.forEach(counter => {
        newCounterValues[counter.name] = counter.value;
      });
      
      setTreeCounters(filteredTreeCounters);
      setCounterValues(newCounterValues);
    } catch (error) {
      setTreeCounters([]);
      message.error('Impossible de charger les compteurs');
      console.error('Error fetching tree counters:', error);
    }
    setLoading(false);
  }, [API_URL, allowed]);

  useEffect(() => { fetchTreeCounters(); }, [fetchTreeCounters]);

  const handleInputChange = (name, value) => {
    // Limit input to 8 characters
    if (value.toString().length <= 8) {
      setCounterValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSetTreeCounter = async (name) => {
    if (!allowed(name) || !allowed(name, 'update')) return;
    if (!/^\d{1,8}$/.test(String(counterValues[name]))) {
      message.error('Saisissez un entier positif ou nul, de 8 chiffres maximum.');
      return;
    }
    try {
      const value = counterValues[name];
      await axios.post(`${API_URL}/counter/set/${name}`, { value: parseInt(value) });
      message.success(`Compteur "${name}" enregistré`);
      // We don't refresh the entire list, which would cause the table to reload
    } catch (error) {
      message.error(`Impossible de modifier le compteur "${name}"`);
      console.error('Error updating tree counter:', error);
    }
  };

  const columns = [
    {
      title: 'Compteur',
      dataIndex: 'name',
      key: 'name',
      render: name => ({ avoir1: 'Compteur avoir (AV1)', avoir3: 'Compteur avoir3 (AV3)' }[name] || name),
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: '40%',
    },
    {
      title: 'Valeur actuelle',
      dataIndex: 'name', // Using name as the key to look up in our local state
      key: 'value',
      sorter: (a, b) => counterValues[a.name] - counterValues[b.name],
      width: '60%',
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Input
            aria-label={`Valeur ${name}`}
            disabled={!allowed(name, 'update')}
            type="number"
            value={counterValues[name]}
            onChange={(e) => handleInputChange(name, e.target.value)}
            style={{ width: '200px' }} // Increased width from 100px to 200px
            onPressEnter={() => handleSetTreeCounter(name)}
            maxLength={8} // Added maxLength property to limit input
          />
          <Button
            aria-label={`Enregistrer ${name}`}
            disabled={!allowed(name, 'update')}
            type="primary"
            onClick={() => handleSetTreeCounter(name)}
            style={{ marginLeft: '10px' }}
          >
            Enregistrer
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>Debugging</Title>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={fetchTreeCounters} 
            loading={loading}
          >
            Actualiser les compteurs
          </Button>
        </div>
        
        <Divider />
        
        <Text type="secondary">
          Consultez et réglez les compteurs auxquels vous avez accès.
        </Text>
        
        <Table
          loading={loading}
          dataSource={treeCounters.filter(counter => allowed(counter.name))}
          columns={columns}
          rowKey="id"
          pagination={false}
          bordered
        />
      </Space>
    </Card>
  );
};

export default Debugging;
